import moment from 'moment';
import { SearchValuesType } from '~/services';
import { enums } from '~/util/enums';

export function prepareSearchQueryString(
    fields: Partial<SearchValuesType & { redirect: boolean }>,
    forApi = false
) {
    const params = [];
    if (fields.firstName) params.push(`first_name=${fields.firstName}`);
    if (fields.lastName) params.push(`last_name=${fields.lastName}`);
    if (fields.DOB) {
        if (typeof fields.DOB === 'object') {
            params.push(`dob=${fields.DOB.toISOString().split('T')[0]}`);
        } else if (
            typeof fields.DOB === 'string' &&
            fields.DOB.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})/)
        ) {
            // Only use the DOB if it is a somewhat valid date string
            params.push(`dob=${fields.DOB.split('T')[0]}`);
        }
    }
    if (fields.memberID) params.push(`insurance_plans.insco_id=${fields.memberID}`);
    if (fields.phone) params.push(`phones.number=${fields.phone}`);
    if (fields.GMPI) params.push(`gmpi=${fields.GMPI}`);
    if (fields.planType && fields.planType?.value !== null) {
        params.push(`is_lite_plan=${fields.planType.value}`);
    }
    if (fields.riskBand) {
        fields.riskBand.forEach((x) => {
            params.push(`risk_band_levels[]=${x.value}`);
        });
    }
    if (fields.riskFactor && fields.riskFactor?.value !== null) {
        params.push(`is_social_risk=${fields.riskFactor.value}`);
    }
    if (fields.disenrolled && fields.disenrolled?.value !== null) {
        params.push(`disenrolled=${fields.disenrolled.value}`);
    }
    if (fields.openGaps && fields.openGaps.length > 0) {
        if (forApi) {
            // When sending open gaps selections to the api, format appropriately
            fields.openGaps.forEach((x) => {
                params.push(
                    `&${enums(['gap', 'type', x.type]).toLowerCase().replace(' ', '_')}s.gap.id[]=${
                        x.id
                    }`
                );
            });
        } else {
            // Since we don't know the gap options list without performing a query, we store
            // the whole selected values object in the params to pass between pages and maintain
            // selections on reloads and such.
            params.push(`openGaps=${btoa(JSON.stringify(fields.openGaps))}`);
        }
    }
    if (fields.insurance && fields.insurance.length > 0) {
        if (forApi) {
            fields.insurance.forEach((x) => {
                params.push(`insurances.id[]=${x.value}`);
            });
        } else {
            params.push(`insurance=${btoa(JSON.stringify(fields.insurance))}`);
        }
    }
    if (fields.clinic) {
        fields.clinic.forEach((x) => {
            params.push(`clinics.id[]=${x.value}`);
        });
    }
    if (fields.PCP) {
        fields.PCP.forEach((x) => {
            params.push(`pcps.id[]=${x.value}`);
        });
    }
    if (fields.redirect === false) params.push(`redirect=${fields.redirect}`);
    return params.join('&');
}

export const planTypes = [
    { value: null, label: 'All' },
    { value: true, label: 'Lite Only' },
    { value: false, label: 'Non Lite Only' },
];

export const riskBandLevels = [
    { value: 5, label: '5' },
    { value: 4, label: '4' },
    { value: 3, label: '3' },
    { value: 2, label: '2' },
    { value: 1, label: '1' },
];

export const riskFactorOptions = [
    { value: null, label: 'All' },
    { value: false, label: 'No Social Risk Factor (MY)' },
    { value: true, label: 'Social Risk Factor (MY)' },
];

export const pcpAssignmentOptions = [
    { value: null, label: 'Currently Assigned' },
    { value: true, label: 'Previously Assigned (last month)' },
];

export const queryFieldMap: {
    [key: string]: 'firstName' | 'lastName' | 'DOB' | 'GMPI' | 'memberID' | 'phone';
} = {
    first_name: 'firstName',
    last_name: 'lastName',
    dob: 'DOB',
    gmpi: 'GMPI',
    'insurance_plans.insco_id': 'memberID',
    'phones.number': 'phone',
};

export function populateSearchFromQuery(
    filters: string[][],
    prev: SearchValuesType
): SearchValuesType {
    filters.forEach(([key, value]) => {
        if (key === 'is_lite_plan') {
            prev.planType =
                planTypes.find((ptype) => ptype.value === JSON.parse(value)) || planTypes[0];
        } else if (key === 'risk_band_levels[]') {
            if (!Array.isArray(prev.riskBand)) prev.riskBand = [];
            if (!prev.riskBand.some((x) => x.value === JSON.parse(value))) {
                const riskBand = riskBandLevels.find((x) => x.value === JSON.parse(value));
                if (riskBand) prev.riskBand.push(riskBand);
            }
        } else if (key === 'openGaps' || key === 'insurance') {
            prev[key] = JSON.parse(atob(value));
        } else if (key === 'is_social_risk') {
            prev.riskFactor =
                riskFactorOptions.find((option) => option.value === JSON.parse(value)) ||
                riskFactorOptions[0];
        } else if (key in queryFieldMap) prev[queryFieldMap[key]] = value;
    });
    return { ...prev };
}

export const searchFieldIsFiltered = (
    field: keyof SearchValuesType,
    initialSearch: SearchValuesType,
    searchValues: Partial<SearchValuesType>
) => {
    const searchVal = searchValues[field];
    const initVal = initialSearch[field];
    if (searchVal === null) return false;
    if (Array.isArray(searchVal) && searchVal.length === 0) return false;

    if (
        typeof searchVal === 'object' &&
        searchVal !== null &&
        'value' in searchVal &&
        typeof initVal === 'object' &&
        initVal !== null &&
        'value' in initVal
    ) {
        return searchVal?.value !== initVal?.value;
    }

    return searchVal !== initVal;
};

export function hasUnsavedFilters(filters: string[][], searchValues: SearchValuesType) {
    return filters.some(([key, value]) => {
        if (key === 'is_lite_plan') {
            return searchValues.planType.value !== JSON.parse(value);
        }
        if (key === 'risk_band_levels[]') {
            return (
                !Array.isArray(searchValues.riskBand) ||
                !searchValues.riskBand.some((x) => x.value === JSON.parse(value))
            );
        }
        if (key === 'is_social_risk') {
            return searchValues.riskFactor.value !== JSON.parse(value);
        }
        if (key === 'openGaps' || key === 'insurance') {
            return JSON.stringify(searchValues[key]) !== atob(value);
        }
        if (key === 'dob') {
            const searchValueDob = searchValues[queryFieldMap[key]]
                ? moment(searchValues[queryFieldMap[key]]).format('YYYY-MM-DD')
                : searchValues[queryFieldMap[key]];
            return searchValueDob !== value;
        }
        if (!queryFieldMap?.[key]) return false;
        return searchValues[queryFieldMap[key]] !== value;
    });
}
